/**
 * OCR Service — Smart Router with Server-Side Rate Limit Awareness
 *
 * Production / Vercel: calls /api/ocr (serverless proxy)
 *   - API key is server-side only
 *   - Rate limits enforced by server per buyerId + IP
 *
 * Local dev (react-scripts start): calls Gemini directly using REACT_APP_GEMINI_API_KEY.
 *
 * @param {string} base64Image - Full base64 data URL
 * @param {string} buyerId     - Authenticated user's buyer ID (sent to server for per-user limiting)
 */

const IS_DEV = process.env.NODE_ENV === 'development';
const GEMINI_DEV_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash-lite:generateContent?key=${GEMINI_DEV_KEY}`;

const EXTRACTION_PROMPT = `You are an AI assistant that extracts order information from e-commerce order screenshots (Amazon, Flipkart, Nykaa, Myntra, Meesho, etc.).

Analyze the provided image and extract the following fields:
1. orderId — The order number/ID (e.g., "407-6038794-5213943" on Amazon, or similar on other platforms)
2. productName — The full product name listed in the order
3. brandName — The brand name of the product. Extract this from the product title (usually the first word or words, e.g., "CareFoam"). "Sold by:" indicates the seller, NOT the brand name. Do NOT use the seller name as the brandName.
4. orderTotal — The final amount paid/grand total in numbers only (no currency symbols or commas)

Return ONLY a valid JSON object with exactly these four keys. If a field cannot be found or read clearly, set it to null.

Example output:
{
  "orderId": "407-6038794-5213943",
  "productName": "RelieVein by Greenatva - Massage Oil for Varicose Veins",
  "brandName": "Greenatva",
  "orderTotal": "541.65"
}

Important rules:
- Return ONLY the JSON object, no explanation, no markdown, no code blocks
- For orderTotal, use the Grand Total or final amount actually paid (after discounts/promotions)
- If you see multiple products, extract the first/primary one
- Do NOT include ₹, $, or any currency symbol in orderTotal
- Do NOT include commas in orderTotal (e.g., use "1234.56" not "1,234.56")
`;

/** Parse raw Gemini data into a structured result. */
function parseGeminiData(geminiData) {
  const rawText = geminiData?.candidates?.[0]?.content?.parts?.[0]?.text || '';

  let extracted;
  try {
    const cleaned = rawText.trim()
      .replace(/^```json?\n?/i, '')
      .replace(/\n?```$/i, '')
      .trim();
    extracted = JSON.parse(cleaned);
  } catch {
    return {
      success: false,
      error: 'PARSE_ERROR',
      message: 'Could not read the order details from this screenshot. The image may be unclear or not a valid order screenshot.',
      rawText,
    };
  }

  const requiredFields = ['orderId', 'productName', 'brandName', 'orderTotal'];
  const fieldLabels = {
    orderId: 'Order Number',
    productName: 'Product Name',
    brandName: 'Brand Name',
    orderTotal: 'Order Total Amount',
  };

  const missingFields = requiredFields.filter(
    (f) => !extracted[f] || String(extracted[f]).trim() === ''
  );

  if (missingFields.length > 0) {
    return {
      success: false,
      error: 'MISSING_FIELDS',
      missingFields: missingFields.map((f) => fieldLabels[f]),
      message: 'Could not extract all required fields from the screenshot.',
      partialData: extracted,
    };
  }

  const sanitizedTotal = String(extracted.orderTotal).replace(/[₹$£€,]/g, '').trim();

  return {
    success: true,
    data: {
      orderId: String(extracted.orderId).trim(),
      productName: String(extracted.productName).trim(),
      brandName: String(extracted.brandName).trim(),
      orderTotal: sanitizedTotal,
    },
  };
}

/**
 * Analyzes an order screenshot.
 * @param {string} base64Image
 * @param {string} buyerId - User's buyer ID (used for server-side per-user rate limiting)
 * @returns {Promise<{ success, data?, error?, message?, remaining?, limit?, resetAt?, resetInMs? }>}
 */
export const analyzeOrderScreenshot = async (base64Image, buyerId = '') => {
  try {
    // ── PRODUCTION: backend proxy (server-side rate limit + hidden API key) ──
    if (!IS_DEV) {
      const response = await fetch('/api/ocr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64Image, buyerId }),
      });

      // Read quota from response headers
      const limitHeader = response.headers.get('X-RateLimit-Limit');
      const remainingHeader = response.headers.get('X-RateLimit-Remaining');
      const resetHeader = response.headers.get('X-RateLimit-Reset');
      const quotaFromHeader = {
        limit: limitHeader ? parseInt(limitHeader, 10) : null,
        remaining: remainingHeader ? parseInt(remainingHeader, 10) : null,
        resetAt: resetHeader ? parseInt(resetHeader, 10) : null,
      };

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Rate limit exceeded (our own server-side limit)
        if (response.status === 429 && result.error === 'RATE_LIMIT_EXCEEDED') {
          return {
            success: false,
            error: 'RATE_LIMIT_EXCEEDED',
            message: result.message,
            remaining: 0,
            limit: result.limit,
            resetAt: result.resetAt,
            resetInMs: result.resetInMs,
            resetLabel: result.resetLabel,
            limitedBy: result.limitedBy,
          };
        }
        // Gemini's own quota (different from our limit)
        if (response.status === 429 || result.error === 'QUOTA_EXCEEDED') {
          return {
            success: false,
            error: 'QUOTA_EXCEEDED',
            message: result.message || 'The AI service is currently busy. Please wait and try again.',
            waitTime: result.waitTime || 60,
          };
        }
        if (result.error === 'API_KEY_MISSING') {
          return { success: false, error: 'API_KEY_MISSING', message: result.message };
        }
        return {
          success: false,
          error: 'API_ERROR',
          message: result.message || 'AI service is temporarily unavailable.',
        };
      }

      // Attach quota data to the success result so the caller can sync the UI
      const parsed = parseGeminiData(result.geminiData);
      return {
        ...parsed,
        quota: quotaFromHeader,
      };
    }

    // ── LOCAL DEV: call Gemini directly ───────────────────────────────────────
    if (!GEMINI_DEV_KEY) {
      return {
        success: false,
        error: 'API_KEY_MISSING',
        message: 'Add REACT_APP_GEMINI_API_KEY to your .env file for local development.',
      };
    }

    const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, '');
    const mimeType = base64Image.match(/^data:(image\/\w+);base64,/)?.[1] || 'image/jpeg';

    const requestBody = {
      contents: [{
        parts: [
          { text: EXTRACTION_PROMPT },
          { inline_data: { mime_type: mimeType, data: base64Data } },
        ],
      }],
      generationConfig: { temperature: 0.1, topK: 1, topP: 1, maxOutputTokens: 512 },
    };

    const response = await fetch(GEMINI_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData?.error?.message || `API error: ${response.status}`;
      if (response.status === 429 || errorMsg.toLowerCase().includes('quota exceeded')) {
        const retryMatch = errorMsg.match(/retry in ([\d\.]+)s/);
        const waitSeconds = retryMatch ? Math.ceil(parseFloat(retryMatch[1])) : 60;
        return { success: false, error: 'QUOTA_EXCEEDED', message: 'The AI service is currently busy. Please wait a moment and try again.', waitTime: waitSeconds };
      }
      return { success: false, error: 'API_ERROR', message: 'AI service is temporarily unavailable. Please try again.' };
    }

    const data = await response.json();
    return parseGeminiData(data);

  } catch (err) {
    console.error('OCR Service Error:', err);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Network error while connecting to AI service. Please check your internet connection.',
    };
  }
};
